import type { Map as ImMap, List } from 'immutable';

import { type Account, accountsSelectors } from 'app-components/flux/accounts';
import { categoriesSelectors } from 'app-components/flux/categories';
import type { SplitItemProps, SplitProps } from 'app-components/flux/transactions/transactionsTypes';
import { getLength, reduce } from 'app-components/utils/core/immutableHelpers';
import store from 'app-components/utils/redux-store';
import type RootState from 'app-components/utils/redux-store/rootState';

import { type Category, CategoryTypeEnum } from '../categories/categoriesTypes';
import * as chartOfAccountsTypes from './chartOfAccountsTypes';

export const isCoaSettable = (coa: chartOfAccountsTypes.ChartOfAccountProps) =>
  coa.type !== chartOfAccountsTypes.CoaTypeEnum.CATEGORY_USAGE_TYPE &&
  coa.type !== chartOfAccountsTypes.CoaTypeEnum.DISPLAY_NODE;

export function getCoaId(coa?: { id?: string; type?: string } | null, allowBlank = false) {
  if (!coa || !coa.id) {
    return allowBlank ? 'BLANK' : chartOfAccountsTypes.CoaTypeEnum.UNCATEGORIZED;
  }
  switch (coa.type) {
    case chartOfAccountsTypes.CoaTypeEnum.ACCOUNT:
      return coa.id;
    case chartOfAccountsTypes.CoaTypeEnum.BALANCE_ADJUSTMENT:
      return `${chartOfAccountsTypes.CoaTypeEnum.BALANCE_ADJUSTMENT}_${coa.id}`;
    case chartOfAccountsTypes.CoaTypeEnum.CATEGORY:
      return coa.id;
    case chartOfAccountsTypes.CoaTypeEnum.NONE:
      return coa.type;
    case chartOfAccountsTypes.CoaTypeEnum.UNCATEGORIZED:
    default:
      return chartOfAccountsTypes.CoaTypeEnum.UNCATEGORIZED;
  }
}

export function getBalanceAdjustmentString(id: string) {
  switch (id) {
    case chartOfAccountsTypes.BalanceAdjustmentTypeEnum.BALANCE_ADJUSTMENT:
      return 'Balance Adjustment';
    case chartOfAccountsTypes.BalanceAdjustmentTypeEnum.OPENING_BALANCE:
      return 'Opening Balance';
    case chartOfAccountsTypes.BalanceAdjustmentTypeEnum.TRANSFER:
      return 'Transfer';
    case chartOfAccountsTypes.BalanceAdjustmentTypeEnum.CREDIT_CARD_PAYMENT:
      return 'Credit Card Payment';
    default:
      assert(false, `unknown coa.id for coa.type=BALANCE_ADJUSTMENT, coa.id=${id}`);
      return `ErrId:=>${String(id)}<=`;
  }
}

export const coaIsBalanceAdjustmentType = (coa?: chartOfAccountsTypes.ChartOfAccountProps | null) =>
  coa && coa.type === chartOfAccountsTypes.CoaTypeEnum.BALANCE_ADJUSTMENT;

export function isL1TransferCoa(coa?: chartOfAccountsTypes.ChartOfAccountProps | null) {
  return (
    coa &&
    coa.type === chartOfAccountsTypes.CoaTypeEnum.BALANCE_ADJUSTMENT &&
    coa.id === chartOfAccountsTypes.BalanceAdjustmentTypeEnum.TRANSFER
  );
}

export const TRANSFER_NOT_FOUND_COA_ID = '24';

export function coasAreEqual(
  coa1?: chartOfAccountsTypes.ChartOfAccountProps | null,
  coa2?: chartOfAccountsTypes.ChartOfAccountProps | null,
) {
  if (!coa1 && !coa2) return true;
  if (!coa1 || !coa2) return false;
  return coa1.type === coa2.type && coa1.id === coa2.id;
}

// we don't support transactions for Loan or Investment accounts
// so we treat them as NOT FOUND
export function isTransferCoaNotFound(
  coa?: chartOfAccountsTypes.ChartOfAccountProps | null,
  state: RootState = store!.getState(),
) {
  const accountsById = accountsSelectors.getAccountsByIdIncludingGoals(state);

  if (coa && coa.type === chartOfAccountsTypes.CoaTypeEnum.ACCOUNT) {
    return accountsById && !accountsById.has(coa.id);
  }
  return false;
}

export function isUnsyncedTransferCoa(
  coa: chartOfAccountsTypes.ChartOfAccountProps | null | undefined,
  state?: RootState,
) {
  if (coa && coa.type === chartOfAccountsTypes.CoaTypeEnum.ACCOUNT) {
    return isTransferCoaNotFound(coa, state);
  }
  return false;
}

export function isSupportedTransferCoa(
  coa?: chartOfAccountsTypes.ChartOfAccountProps | null,
  state: RootState = store!.getState(),
) {
  const accountsById = accountsSelectors.getAccountsByIdIncludingGoals(state);
  if (coa && coa.type === chartOfAccountsTypes.CoaTypeEnum.ACCOUNT) {
    const account = accountsById?.get(coa.id);
    return (
      !isUnsyncedTransferCoa(coa, state) &&
      account &&
      account?.type !== 'INVESTMENT' && // TODO: should we support transfers to investment accounts?
      (account?.type !== 'LOAN' || !account?.isConnected) // manual loan accounts supporte transactions, but connected loan accounts do not
    );
  }
  return false;
}

export const isTransferCOA = (coa?: chartOfAccountsTypes.ChartOfAccountProps | null) =>
  coa &&
  (coa.type === chartOfAccountsTypes.CoaTypeEnum.ACCOUNT ||
    coa.type === chartOfAccountsTypes.CoaTypeEnum.BALANCE_ADJUSTMENT);

/**
 * Whether `coa` belongs on a category picker scoped to `targetCategoryType` — shared by every
 * series-setup flow (mobile's Bill Scout `SelectCoaStack` filters, web's `SeriesDetailContent`)
 * that restricts manual category entry to one side (income vs. expense) of a two-stage setup.
 * Transfers are always eligible on either side. Uncategorized is deliberately treated as
 * expense-only: series classification (see `getParentTypeFromCoa`-style logic) already treats it
 * as bill/expense-like, so allowing it on the income side would let a manual entry silently
 * become a Bill — the bug this predicate exists to prevent.
 */
export const isCoaEligibleForCategoryType = (
  coa: chartOfAccountsTypes.ChartOfAccountProps | null | undefined,
  targetCategoryType: CategoryTypeEnum,
  categoriesById: ImMap<string, Category>,
): boolean => {
  if (!coa || isTransferCOA(coa)) return true;
  if (coa.type === chartOfAccountsTypes.CoaTypeEnum.CATEGORY) {
    return categoriesById.get(coa.id)?.type === targetCategoryType;
  }
  if (coa.type === chartOfAccountsTypes.CoaTypeEnum.UNCATEGORIZED) {
    return targetCategoryType === CategoryTypeEnum.EXPENSE;
  }
  return true;
};

type CoaTypeAndId = Pick<chartOfAccountsTypes.ChartOfAccountProps, 'type' | 'id'>;

const TRANSFER_OR_CREDIT_CARD_PAYMENT_BALANCE_ADJUSTMENT_IDS: ReadonlySet<string> = new Set([
  chartOfAccountsTypes.BalanceAdjustmentTypeEnum.TRANSFER,
  chartOfAccountsTypes.BalanceAdjustmentTypeEnum.CREDIT_CARD_PAYMENT,
]);

const isTransferOrCreditCardPaymentBalanceAdjustmentCoa = (coa?: CoaTypeAndId | null): boolean =>
  Boolean(
    coa &&
      coa.type === chartOfAccountsTypes.CoaTypeEnum.BALANCE_ADJUSTMENT &&
      TRANSFER_OR_CREDIT_CARD_PAYMENT_BALANCE_ADJUSTMENT_IDS.has(coa.id),
  );

export const isTransferOrCreditCardPaymentCoa = (coa?: CoaTypeAndId | null): boolean =>
  Boolean(
    coa &&
      (coa.type === chartOfAccountsTypes.CoaTypeEnum.ACCOUNT || isTransferOrCreditCardPaymentBalanceAdjustmentCoa(coa)),
  );

// The balance-adjustment ids that behave like a transfer/credit-card-payment for exclusion-default
// purposes (Spending Plan / Reports auto-exclusion). It reuses the balance-adjustment predicate
// above while deliberately including ordinary balance adjustments.
export const isTransferLikeBalanceAdjustmentCoa = (coa?: CoaTypeAndId | null): boolean =>
  Boolean(
    coa &&
      coa.type === chartOfAccountsTypes.CoaTypeEnum.BALANCE_ADJUSTMENT &&
      (coa.id === chartOfAccountsTypes.BalanceAdjustmentTypeEnum.BALANCE_ADJUSTMENT ||
        isTransferOrCreditCardPaymentBalanceAdjustmentCoa(coa)),
  );

const isCategory = (splitTxn: SplitItemProps) => splitTxn.coa?.type === 'CATEGORY';

export const disableExcludeReports = (
  coa?: chartOfAccountsTypes.ChartOfAccountProps | null,
  split?: SplitProps | null,
) => {
  if (!split || !split.items) {
    return coa?.type === 'BALANCE_ADJUSTMENT' && (coa?.id === '0' || coa?.id === '1');
  }

  if (Array.isArray(split.items)) {
    return split.items.filter(isCategory).length === 0;
  }

  return split.items.filter(isCategory).size === 0;
};

export const getCoaString = (
  coa: chartOfAccountsTypes.ChartOfAccountProps | null | undefined,
  categoriesById: ImMap<string, Category>,
  accountsById: ImMap<string, Account>,
  noParent = false,
) => {
  let coaString;
  switch (coa?.type) {
    case chartOfAccountsTypes.CoaTypeEnum.UNCATEGORIZED:
      coaString = 'Uncategorized';
      break;
    case chartOfAccountsTypes.CoaTypeEnum.ACCOUNT:
      {
        const acct = accountsById?.get(coa.id);
        coaString = acct?.name || 'Unsynced Account';
      }
      break;
    case chartOfAccountsTypes.CoaTypeEnum.CATEGORY:
      coaString = categoriesSelectors.getCategoryString(categoriesById, coa.id, noParent);
      break;
    case chartOfAccountsTypes.CoaTypeEnum.BALANCE_ADJUSTMENT:
      coaString = getBalanceAdjustmentString(coa.id);
      break;
    default:
      coaString = 'NA';
  }
  return coaString;
};

export const findCoaNode = (
  coa: chartOfAccountsTypes.ChartOfAccountProps,
  nodes:
    | ReadonlyArray<chartOfAccountsTypes.ChartOfAccountNodeProps>
    | List<chartOfAccountsTypes.ChartOfAccountNodeProps>,
): chartOfAccountsTypes.ChartOfAccountNodeProps | null => {
  return reduce(nodes, null as chartOfAccountsTypes.ChartOfAccountNodeProps | null, (acc, node) => {
    if (acc) return acc;
    if (coasAreEqual(node.coa ?? undefined, coa)) {
      return node;
    }
    if (getLength(node.children ?? []) > 0) {
      return findCoaNode(coa, node.children ?? []) ?? acc;
    }
    return acc;
  });
};
